<template>
  <div v-if="!practiceCourt.visible">
    <Card
      :content="content"
      :characters="characters"
      :label="content.type === 'desktop' ? '云桌面' : '练习'"
      @clickCard="handleClick"
    />
  </div>
</template>

<script lang="ts">
import { Content, Sequence } from '@forchange/storytime';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import SequenceQueue from '@/utils/portal/SequenceQueue';
import Card from './Card.vue';
import initSpecial, { PracticeCourtOptions, PracticeData } from './initSpecial';
import { namespace } from 'vuex-class';
import { IDesktopOptions, PracticeCourt as PracticeCourtData } from '@/store/modules/common/types';
import { Environment } from '@/store/modules/common/types/index';
import { getSandboxIframeUrl } from '../SandboxIframe/util';
import { getChatGPTIframeUrl } from '../ChatGPTIframe/util';
import { getAIPaintIframeUrl } from '../AIPaintIframe/util';

const userModule = namespace('common');

@Component({
  components: {
    Card
  }
})
export default class PracticeCourt extends Vue {
  @Prop({ type: Object }) content!: Content & {
    path: string;
    desc: string;
    name: string;
    autoJump: boolean;
    portalSequence: Sequence;
    // 注意同步UpdatePracticeCourt
    type: 'url' | 'vika' | 'desktop' | 'sandbox' | 'jupyter' | 'chatgpt' | 'aipaint';
    options: PracticeCourtOptions | undefined;
    iframePath: string | undefined;
    SaveIframeUrl: string | undefined;
    userId: string | undefined;
    title: string;
    viewPercent: number | undefined;
    clientResizePercent: string | undefined;
    desktopOptions?: IDesktopOptions;
  };
  @Prop({ type: Array }) characters!: Array<{
    name: string;
    avatar: string;
    direction?: 'right' | 'left';
  }>;

  @userModule.State('practiceCourt') practiceCourt!: PracticeCourtData;
  @userModule.State('environment') environment!: Environment;

  mounted() {
    if (!['是', 'true'].includes(this.content.autoJump?.toString())) return;
    this.$nextTick(() => {
      if (this.content.portalSequence) return;
      this.call(true);
    });
  }

  async call(autoJump?: boolean) {
    await this.arouseCleword();

    window.sequenceStack.push(new SequenceQueue(this.content.$id));

    const isSuccess = window.storyteller.call({
      path: this.content.path,
      notAotuCall: true,
      prop: {
        scrollTop: (this.$parent?.$refs?.scroller as HTMLElement).scrollTop,
        autoJump,
        practiceCourtView: true
      }
    });
    if (!isSuccess) {
      this.$Message.error({
        content: '没有找到对应的文稿，请检查路径是否正确',
        time: 3000
      });
      await this.$PostConnection?.command('close_double_column');
      this.$store.commit('common/setPracticeCourtVisible', false);
      return;
    }
    window.next();
  }

  async arouseCleword(iframeData?: PracticeData) {
    // 接口请求拿到模板链接
    this.$PostConnection?.command('loading', {
      delay: 0
    });
    this.$PostConnection?.command('close_double_column', {
      delay: 0
    });

    try {
      const practiceData = iframeData || (await this.getIframeUrl());
      if (practiceData?.url) {
        const postMessage = {
          iframeUrl: practiceData?.url,
          label: this.content.type === 'desktop' ? '云桌面' : '练习',
          title: this.content.title,
          viewPercent: this.content.viewPercent || 40,
          clientResizePercent: !['禁用', 'disable'].includes(
            this.content.clientResizePercent || ''
          ),
          type: this.content.type,
          options: this.content.options,
          desktopOptions: practiceData?.instanceId && {
            instanceId: practiceData?.instanceId,
            ...(this.content.options?.desktop || {})
          }
        };
        if (!this.$PostConnection) return;

        await this.$PostConnection.command('open_double_column', { ...postMessage, delay: 0 });
        this.$store.commit('common/setPracticeCourtVisible', true);

        this.$store.commit('common/setPracticeCourt', {
          ...postMessage,
          userId: practiceData?.userId,
          practiceCourtId: this.content.$id,
          practiceCourtKind: this.content.$kind
        });
      }
    } catch (error) {
      this.$Message.error({
        time: 3000,
        content: '获取练习场分栏地址有误啦，请找客服寻求帮助'
      });
      console.log(error);
    } finally {
      this.$PostConnection?.command('close_loading');
    }
  }

  async getIframeUrl() {
    if (this.content.type === 'aipaint') {
      const url = getAIPaintIframeUrl(this.content?.options?.aipaint);
      return {
        userId: '',
        url
      };
    } else if (this.content.type === 'chatgpt') {
      const url = await getChatGPTIframeUrl(this.content?.options?.chatgpt);
      return {
        userId: '',
        url
      };
    } else if (this.content.type === 'sandbox') {
      const sandboxData = {
        ...this.content,
        ...this.content.options?.sandbox
      };

      const url = getSandboxIframeUrl(sandboxData);
      return {
        userId: '',
        url
      };
    } else if (this.content.type === 'url') {
      if (!this.content.options?.url?.url) {
        this.$Message.error({
          content: '「练习场」参数对应地址要填写完整哦！',
          time: 5000
        });
        return;
      }

      return {
        url: this.content.options.url.url
      };
    }

    const courseId =
      this.environment &&
      this.environment.kind === 'public-preview' &&
      this.environment.sharedKey.length === 24
        ? this.environment.sharedKey
        : '';

    const params = {
      type: this.content.type,
      options: this.content.options ?? {},
      userId: this.content.userId,
      courseId
    };

    return await initSpecial.call(this, params);
  }

  /**
   * 再次渲染尝试插入记录
   */
  @Watch('content.$id', {
    immediate: true
  })
  setPortalEnd() {
    const sequenceQueue = window.sequenceStack.peek();
    if (sequenceQueue?.portalId !== this.content.$id) return;
    window.sequenceStack.pop();

    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        portalSequence: {
          ...sequenceQueue?.getFront(),
          practiceCourtView: true
        }
      }
    });
  }

  async handleClick() {
    // 已存在记录

    if (this.content.portalSequence) {
      if (this.content.type === 'vika') {
        await this.arouseCleword({
          url: this.content.SaveIframeUrl || '',
          userId: this.content.userId || ''
        });
      } else {
        await this.arouseCleword();
      }

      this.$store.commit('common/setPortal', {
        ...this.content.portalSequence,
        scrollTop: (this.$parent?.$refs?.scroller as HTMLElement).scrollTop
      });

      this.$PostConnection?.command('close_loading');
      return;
    }

    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        autoJump: false
      }
    });

    this.$nextTick(() => {
      this.call();
      this.$emit('updateHistory');
    });
  }
}
</script>
